import React, { useEffect, useRef } from 'react';
import Carousel from './Carousel';
import { AWNING_WARRANTY_YEARS, EXPECTED_LIFE_YEARS } from '../config/warranty';
import { TOTAL_COLOURWAYS } from '../data/sauledaColours';
import { STOCK_COLOUR_NAME, LEAD_STOCK, MADE_TO_ORDER_BANDS, LEAD_PERGOLA } from '../config/leadTimes';
import './HowItWorks.css';

// Manufacturing/supply-chain story. Each step is a beat in the build:
// 1) fabric  →  2) assembly  →  3) test+ship.
// The card schema is intentionally minimal: title, description, square
// image, optional badge. The Carousel component handles layout.
const STEPS = [
  {
    num: '01',
    title: 'Spanish Sauleda Fabric',
    desc: `Premium acrylic fabric, woven and dyed in Barcelona. ${TOTAL_COLOURWAYS} colourways, ${AWNING_WARRANTY_YEARS}-year warranty, built to last ${EXPECTED_LIFE_YEARS}+ years.`,
    image: '/uploads/gallery/fabric-options.webp',
    imageAlt: 'Folded Sauleda fabric swatches in muted colours',
  },
  {
    num: '02',
    title: 'Assembled in Melbourne',
    desc: 'Frame, fabric and motor combined into a single kit at our Hoppers Crossing warehouse - by the same team who built your quote.',
    image: '/uploads/gallery/step-04-warehouse-assembly.webp',
    imageAlt: 'Hands fitting a motor into an awning cassette on a workbench',
  },
  {
    num: '03',
    title: 'Tested & Shipped',
    desc: 'Every kit is extended, retracted and wind-load-checked before it leaves us. Pallet-wrapped and dispatched directly to your site.',
    badge: 'Quality Check',
    image: '/uploads/gallery/step-05-tested-shipped.webp',
    imageAlt: 'Finished awning kit shrink-wrapped on a pallet for dispatch',
  },
];

// Pure presentational component for a single step. Lifting this out
// makes it trivial to render inside any layout — the Carousel below
// just calls renderItem with a step and gets a card.
function StepCard({ step }) {
  return (
    <article className="hiw-card">
      {step.image && (
        <div data-arrow-anchor className={`hiw-card-img${step.imageFit === 'contain' ? ' hiw-card-img--contain' : ''}`}>
          <img src={step.image} alt={step.imageAlt || step.title} loading="lazy" />
        </div>
      )}
      <div className="hiw-card-body">
        <span className="hiw-card-num" aria-hidden="true">{step.num}</span>
        <h3 className="hiw-card-title">{step.title}</h3>
        {step.badge && <span className="hiw-card-badge">{step.badge}</span>}
        <p className="hiw-card-desc">{step.desc}</p>
      </div>
    </article>
  );
}

// `awningsOnly` is for an awning's own page: the lead-time note then leaves out
// the pergola fabrication time, which only belongs where both ranges are sold.
function HowItWorks({ compact, awningsOnly = false }) {
  const rootRef = useRef(null);

  // Reveal animation on scroll-into-view, applied to .asg-reveal nodes.
  // No carousel state — that's the Carousel component's job.
  useEffect(() => {
    if (!rootRef.current) return undefined;
    const els = rootRef.current.querySelectorAll('.asg-reveal');
    if (!('IntersectionObserver' in window)) {
      els.forEach((el) => el.classList.add('asg-visible'));
      return undefined;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('asg-visible');
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
    );
    els.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={rootRef} className={`hiw${compact ? ' hiw--compact' : ''}`}>
      <div className="hiw-header asg-reveal">
        <p className="hiw-label">How It's Made</p>
        <h2 className="hiw-title">From raw materials to your door.</h2>
      </div>

      <Carousel
        className="hiw-carousel asg-reveal"
        ariaLabel="How it works - steps"
        items={STEPS}
        renderItem={(step) => <StepCard step={step} />}
        visibleCount={{ base: 1, md: 2, lg: 3 }}
        gap={24}
      />

      <div className="hiw-leadtime-wrap">
        <p className="hiw-leadtime-note">
          <strong>Lead time:</strong> {STOCK_COLOUR_NAME} is held in stock and often ships the {LEAD_STOCK};
          everything else is made to order - {MADE_TO_ORDER_BANDS}.{!awningsOnly && <> Aluminium pergolas are fabricated to order in {LEAD_PERGOLA}.</>}
          Every kit is built to your exact width, projection, fabric and finish - assembled, tested and
          dispatched from our Melbourne warehouse.
        </p>
      </div>
    </div>
  );
}

export default HowItWorks;
